/*
  Ambassador / sponsor storefront checkout interceptor.
  Loaded on every protonsports.com page via a Shopify ScriptTag.

  Flow:
    1. On DOM ready, fetch /cart.js and check if any applicable discount
       code starts with "AMB-" (the ambassador comp code).
    2. If yes → we're in ambassador mode. Do two things:
         a) Replace every "Checkout" button with "Submit for Proton Review".
         b) Intercept clicks + <form action="/cart"> submits that would
            take the customer to Shopify's real /checkout, and open the
            review modal instead.
    3. The modal collects name / email / phone / ship-to address.
    4. On submit → POST the cart lines + address to the Hub. Hub creates
       a Shopify DRAFT order tagged `comp, ambassador, ambassador-page`.
       Proton staff opens the draft in Shopify Admin and completes it
       manually (that's when it becomes a real, fulfillable order).
    5. Show a success screen, clear the cart, wipe the discount code.

  Notes:
    - Runs on ALL protonsports.com pages (ScriptTag is global). Bails
      immediately if no AMB- code is on the cart, so normal customers
      never see anything.
    - PROTON_API_BASE is the Cloudflare tunnel that proxies to the Hub.
      Same one used by the athlete-signup snippet. If cloudflared restarts
      and the tunnel URL rotates, update PROTON_API_BASE below and bump
      the Cache-Control max-age at the Hub route to force refresh.
*/
(function () {
  "use strict";

  var PROTON_API_BASE = "https://hub.protonteam.net";
  var AMB_CODE_PREFIX = "AMB-";
  var STATE = { active: false, cart: null, submitting: false };

  function log() {
    try { console.info.apply(console, ["[amb]"].concat([].slice.call(arguments))); } catch (e) {}
  }

  function fetchCart() {
    return fetch("/cart.js", { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  function hasAmbassadorDiscount(cart) {
    if (!cart || !Array.isArray(cart.discount_codes)) return false;
    return cart.discount_codes.some(function (d) {
      return d && d.applicable && typeof d.code === "string" && d.code.indexOf(AMB_CODE_PREFIX) === 0;
    });
  }

  // A visitor is in "ambassador context" if any of these signal we're
  // inside the sponsored / comp gear flow — even before an item is in the
  // cart. That lets us hide payment buttons on the collection index and on
  // product pages under /collections/ambassador-gear/products/... too.
  var AMB_URL_RE = /^\/collections\/ambassador-gear(?:\/|$|\?)/i;
  function urlIsAmbassador() {
    try { return AMB_URL_RE.test(location.pathname + (location.search || "")); }
    catch (e) { return false; }
  }
  function readAmbSession() {
    try { return sessionStorage.getItem("proton_amb_mode") === "1"; }
    catch (e) { return false; }
  }
  function writeAmbSession() {
    try { sessionStorage.setItem("proton_amb_mode", "1"); } catch (e) {}
  }
  function inAmbassadorContext(cart) {
    return hasAmbassadorDiscount(cart) || urlIsAmbassador() || readAmbSession();
  }

  function activate(cart) {
    STATE.active = true;
    STATE.cart = cart;
    writeAmbSession();
    log("ambassador mode active — cart items:", (cart && cart.item_count) || 0, "discount total:", (cart && cart.total_discount) || 0);
    injectStyles();
    interceptCheckoutButtons();
    hookCartEvents();
    // Show a subtle banner explaining what happens on checkout — but only
    // once the cart actually has the AMB- code, so a random visitor who
    // wanders onto /collections/ambassador-gear doesn't see the "$0 to
    // you" copy without being an actual ambassador.
    if (hasAmbassadorDiscount(cart)) showBanner();
  }

  function refresh() {
    fetchCart().then(function (cart) {
      if (!cart) return;
      var wasActive = STATE.active;
      var isActive = inAmbassadorContext(cart);
      STATE.cart = cart;
      if (isActive && !wasActive) return activate(cart);
      if (isActive && wasActive) {
        // Cart changed — re-scan for new checkout buttons that may have
        // appeared (e.g. after a drawer opened) and re-apply the AMB
        // class in case the theme swapped out <html>.
        document.documentElement.classList.add("amb-mode");
        interceptCheckoutButtons();
        // Promote to full banner once the AMB- code actually applies.
        if (hasAmbassadorDiscount(cart)) showBanner();
      }
      if (!isActive && wasActive) {
        STATE.active = false;
        document.documentElement.classList.remove("amb-mode");
        log("ambassador context no longer active");
      }
    });
  }

  function injectStyles() {
    if (document.getElementById("amb-styles")) return;
    var s = document.createElement("style");
    s.id = "amb-styles";
    s.textContent = [
      ".amb-banner{position:sticky;top:0;z-index:9998;background:linear-gradient(135deg,#0a0612,#1b0f2e);color:#fbbf24;text-align:center;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;letter-spacing:.04em;padding:10px 14px;border-bottom:1px solid rgba(251,191,36,.35);box-shadow:0 6px 18px -8px rgba(0,0,0,.4);}",
      ".amb-banner b{color:#f472b6;}",
      ".amb-overlay{position:fixed;inset:0;background:rgba(6,4,15,.72);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);z-index:9999;display:flex;align-items:center;justify-content:center;padding:20px;font-family:'Inter',system-ui,sans-serif;}",
      ".amb-modal{background:#0a0612;background:radial-gradient(600px 400px at 10% 0%,rgba(251,191,36,.15),transparent 60%),radial-gradient(500px 400px at 100% 100%,rgba(139,92,246,.18),transparent 60%),#0a0612;color:#f6f2ff;border:1px solid rgba(255,255,255,.14);border-radius:18px;padding:26px 24px;max-width:520px;width:100%;max-height:92vh;overflow-y:auto;box-shadow:0 30px 80px -20px rgba(0,0,0,.6);}",
      ".amb-modal h2{margin:0 0 6px;font-size:22px;font-weight:800;letter-spacing:-.01em;color:#f6f2ff;}",
      ".amb-modal h2 span{background:linear-gradient(90deg,#fbbf24,#f472b6);-webkit-background-clip:text;background-clip:text;color:transparent;}",
      ".amb-modal p.amb-lede{margin:2px 0 18px;color:#a092c0;font-size:14px;line-height:1.5;}",
      ".amb-eyebrow{font-family:'JetBrains Mono',ui-monospace,monospace;font-size:10px;letter-spacing:.22em;text-transform:uppercase;color:#fbbf24;margin-bottom:6px;}",
      ".amb-field{display:flex;flex-direction:column;gap:6px;margin-bottom:12px;}",
      ".amb-field span{font-size:10px;text-transform:uppercase;letter-spacing:.14em;color:#a092c0;font-family:'JetBrains Mono',ui-monospace,monospace;}",
      ".amb-field input,.amb-field textarea,.amb-field select{background:rgba(0,0,0,.35);border:1px solid rgba(255,255,255,.14);color:#f6f2ff;border-radius:10px;padding:10px 12px;font-size:14px;font-family:'Inter',sans-serif;width:100%;box-sizing:border-box;}",
      ".amb-field input:focus,.amb-field textarea:focus,.amb-field select:focus{outline:none;border-color:#fbbf24;box-shadow:0 0 0 3px rgba(251,191,36,.18);}",
      ".amb-row{display:grid;grid-template-columns:1fr 1fr;gap:10px;}",
      ".amb-row-3{display:grid;grid-template-columns:1.5fr 1fr 1fr;gap:10px;}",
      "@media(max-width:520px){.amb-row,.amb-row-3{grid-template-columns:1fr;}}",
      ".amb-summary{background:rgba(251,191,36,.06);border:1px solid rgba(251,191,36,.28);border-radius:12px;padding:12px 14px;margin:10px 0 16px;font-size:13px;color:#f6f2ff;line-height:1.4;}",
      ".amb-summary .amb-line{display:flex;justify-content:space-between;gap:12px;padding:3px 0;}",
      ".amb-summary .amb-line em{font-style:normal;color:#a092c0;}",
      ".amb-actions{display:flex;gap:10px;margin-top:14px;}",
      ".amb-btn{flex:1;padding:14px 18px;border-radius:12px;font-weight:800;font-size:15px;border:none;cursor:pointer;font-family:'Inter',sans-serif;transition:transform .12s ease,box-shadow .12s ease;}",
      ".amb-btn-primary{background:linear-gradient(135deg,#fbbf24,#f472b6);color:#20140a;box-shadow:0 12px 40px -14px rgba(251,191,36,.55);}",
      ".amb-btn-primary:hover{transform:translateY(-1px);box-shadow:0 18px 48px -14px rgba(251,191,36,.65);}",
      ".amb-btn-primary:disabled{opacity:.55;cursor:not-allowed;transform:none;box-shadow:none;}",
      ".amb-btn-ghost{background:rgba(255,255,255,.06);color:#f6f2ff;border:1px solid rgba(255,255,255,.14);}",
      ".amb-btn-ghost:hover{background:rgba(255,255,255,.1);}",
      ".amb-error{background:rgba(239,68,68,.14);border:1px solid rgba(239,68,68,.45);border-radius:10px;padding:10px 12px;color:#ffb4b4;font-size:12.5px;margin-top:10px;}",
      ".amb-success{text-align:center;padding:12px 4px;}",
      ".amb-success .amb-check{font-size:56px;color:#fbbf24;line-height:1;}",
      ".amb-success h2{color:#fbbf24;font-size:18px;margin:10px 0 6px;}",
      ".amb-success .amb-order{margin-top:14px;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;letter-spacing:.14em;color:#a092c0;}",
      // Ambassador mode: no payment options. The order is $0 to the visitor
      // and completed manually by Proton staff — express-checkout buttons
      // would be misleading and can bypass the review modal entirely, so
      // hide every wallet / dynamic-checkout / express payment surface.
      "html.amb-mode .shopify-payment-button,",
      "html.amb-mode .additional-checkout-buttons,",
      "html.amb-mode [data-shopify=\"dynamic-checkout-cart\"],",
      "html.amb-mode [data-shopify=\"payment-button\"],",
      "html.amb-mode shop-pay-button,",
      "html.amb-mode .shop-pay-button,",
      "html.amb-mode .shop-pay-button-container,",
      "html.amb-mode .wallet-button,",
      "html.amb-mode .paypal-button-container,",
      "html.amb-mode .apple-pay-button,",
      "html.amb-mode .google-pay-button,",
      "html.amb-mode [class*=\"AdditionalCheckoutButtons\"],",
      "html.amb-mode [class*=\"DynamicCheckout\"],",
      "html.amb-mode [class*=\"WalletButton\"],",
      "html.amb-mode [aria-label*=\"Shop Pay\"],",
      "html.amb-mode [aria-label*=\"Apple Pay\"],",
      "html.amb-mode [aria-label*=\"Google Pay\"],",
      "html.amb-mode [aria-label*=\"PayPal\"],",
      "html.amb-mode [aria-label*=\"Amazon Pay\"]{display:none!important;visibility:hidden!important;height:0!important;overflow:hidden!important;}",
    ].join("");
    document.head.appendChild(s);
    // Apply the class immediately so payment surfaces vanish before the
    // theme finishes hydrating them.
    document.documentElement.classList.add("amb-mode");
  }

  function showBanner() {
    if (document.getElementById("amb-banner")) return;
    var b = document.createElement("div");
    b.id = "amb-banner";
    b.className = "amb-banner";
    b.innerHTML = "Ambassador mode &middot; Everything is <b>$0 to you</b> &middot; Checkout submits for Proton review.";
    document.body.insertBefore(b, document.body.firstChild);
  }

  var CHECKOUT_SELECTORS = [
    "button[name='checkout']",
    "input[name='checkout']",
    "a[href*='/checkout']",
    ".shopify-payment-button__button",
    "button[type='submit'][form^='cart']",
  ];

  function interceptCheckoutButtons() {
    if (!STATE.active) return;
    CHECKOUT_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(rewireButton);
    });
    // Also disarm cart <form> submissions.
    document.querySelectorAll("form[action='/cart'],form[action^='/cart?']").forEach(function (f) {
      if (f.__ambBound) return;
      f.__ambBound = true;
      f.addEventListener("submit", function (e) {
        if (!STATE.active) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        openModal();
      }, true);
    });
  }

  function rewireButton(btn) {
    if (!btn || btn.__ambBound) return;
    btn.__ambBound = true;
    // Text swap — leave hidden Buy-with-Shop-Pay etc alone (they don't
    // show the word Checkout).
    var t = (btn.textContent || btn.value || "").toLowerCase();
    if (t.indexOf("checkout") !== -1 || t.indexOf("check out") !== -1) {
      if ("value" in btn && btn.tagName === "INPUT") btn.value = "Submit for Proton Review";
      else btn.textContent = "Submit for Proton Review";
    }
    btn.addEventListener("click", function (e) {
      if (!STATE.active) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openModal();
    }, true);
  }

  function hookCartEvents() {
    // Re-scan when Shopify or the theme fires cart update events.
    ["cart:refresh", "cart:updated", "cart:build"].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(refresh, 200); });
    });
    // Wrap fetch + XHR so we catch Ajax cart mutations.
    var of = window.fetch;
    if (of && !window.__ambFetchWrapped) {
      window.__ambFetchWrapped = true;
      window.fetch = function () {
        var args = arguments;
        var p = of.apply(this, args);
        try {
          var u = (args[0] && args[0].url) || args[0];
          if (typeof u === "string" && /\/cart\/(add|change|update|clear)/.test(u)) {
            p.then(function () { setTimeout(refresh, 250); });
          }
        } catch (e) {}
        return p;
      };
    }
    // MutationObserver picks up dynamically-injected checkout buttons
    // (drawer open, quick-view, ajax cart replacement).
    if (window.MutationObserver && !window.__ambObserver) {
      window.__ambObserver = new MutationObserver(function () {
        if (STATE.active) interceptCheckoutButtons();
      });
      window.__ambObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function summarizeCart(cart) {
    var items = (cart.items || []).map(function (i) {
      return '<div class="amb-line"><span>' + esc(i.quantity) + ' &times; ' + esc(i.product_title || i.title) + (i.variant_title && i.variant_title !== "Default Title" ? ' <em>&middot; ' + esc(i.variant_title) + '</em>' : '') + '</span><em>$0.00</em></div>';
    }).join("");
    var ct = (cart.item_count || 0);
    return items + '<div class="amb-line" style="margin-top:6px;padding-top:8px;border-top:1px solid rgba(255,255,255,.08);"><span><em>' + ct + ' item' + (ct === 1 ? '' : 's') + ' &middot; Proton comp</em></span><em style="color:#fbbf24;font-weight:700">$0.00</em></div>';
  }

  function openModal() {
    if (document.getElementById("amb-overlay")) return;
    if (!STATE.cart) return refresh(); // fetch and try again next cart event
    var cart = STATE.cart;

    var overlay = document.createElement("div");
    overlay.id = "amb-overlay";
    overlay.className = "amb-overlay";
    overlay.innerHTML =
      '<div class="amb-modal" role="dialog" aria-labelledby="amb-title">' +
        '<div class="amb-eyebrow">PROTON SPORTS &middot; AMBASSADOR</div>' +
        '<h2 id="amb-title">Submit your <span>sponsored</span> order</h2>' +
        '<p class="amb-lede">A Proton staffer will review the draft, confirm sizing/inventory, and ship it out. You&#39;ll get a confirmation email when it goes out.</p>' +
        '<div class="amb-summary">' + summarizeCart(cart) + '</div>' +
        '<form id="amb-form" autocomplete="on">' +
          '<div class="amb-row">' +
            '<label class="amb-field"><span>Your name</span><input name="name" type="text" required placeholder="First Last" autocomplete="name" /></label>' +
            '<label class="amb-field"><span>Email</span><input name="email" type="email" required placeholder="you@example.com" autocomplete="email" /></label>' +
          '</div>' +
          '<div class="amb-row">' +
            '<label class="amb-field"><span>Phone (optional)</span><input name="phone" type="tel" placeholder="(555) 123-4567" autocomplete="tel" /></label>' +
            '<label class="amb-field"><span>I&#39;m a…</span>' +
              '<select name="affiliation" required>' +
                '<option value="Ambassador">Ambassador</option>' +
                '<option value="Sponsor">Sponsor</option>' +
                '<option value="Athlete">Athlete / Pro Player</option>' +
                '<option value="Influencer">Influencer / Creator</option>' +
                '<option value="Team">Team / Coach / Club</option>' +
                '<option value="Other">Other</option>' +
              '</select>' +
            '</label>' +
          '</div>' +
          '<label class="amb-field"><span>Street address</span><input name="ship_address1" type="text" required placeholder="123 Main St" autocomplete="address-line1" /></label>' +
          '<div class="amb-row-3">' +
            '<label class="amb-field"><span>City</span><input name="ship_city" type="text" required autocomplete="address-level2" /></label>' +
            '<label class="amb-field"><span>State</span><input name="ship_state" type="text" required maxlength="2" placeholder="CA" autocomplete="address-level1" style="text-transform:uppercase;" /></label>' +
            '<label class="amb-field"><span>ZIP</span><input name="ship_postal" type="text" required maxlength="10" placeholder="90210" autocomplete="postal-code" /></label>' +
          '</div>' +
          '<label class="amb-field"><span>Notes (optional)</span><textarea name="notes" rows="2" placeholder="Sizing, deadline, event…"></textarea></label>' +
          '<div id="amb-error" class="amb-error" style="display:none"></div>' +
          '<div class="amb-actions">' +
            '<button type="button" class="amb-btn amb-btn-ghost" id="amb-cancel">Back to cart</button>' +
            '<button type="submit" class="amb-btn amb-btn-primary" id="amb-submit">Submit for review</button>' +
          '</div>' +
        '</form>' +
      '</div>';
    document.body.appendChild(overlay);

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeModal();
    });
    document.getElementById("amb-cancel").addEventListener("click", closeModal);
    document.getElementById("amb-form").addEventListener("submit", handleSubmit);
  }

  function closeModal() {
    var o = document.getElementById("amb-overlay");
    if (o) o.parentNode.removeChild(o);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (STATE.submitting) return;
    var form = e.target;
    var fd = new FormData(form);
    var errorEl = document.getElementById("amb-error");
    var btn = document.getElementById("amb-submit");
    errorEl.style.display = "none";
    errorEl.textContent = "";

    var cart = STATE.cart || {};
    var items = cart.items || [];
    if (!items.length) {
      errorEl.textContent = "Your cart is empty — add at least one item.";
      errorEl.style.display = "block";
      return;
    }
    var lines = items.map(function (i) {
      return {
        shopify_variant_id: String(i.variant_id || i.id),
        shopify_product_id: String(i.product_id || ""),
        name: i.product_title || i.title || "Item",
        sku: i.sku || "",
        variant_title: (i.variant_title && i.variant_title !== "Default Title") ? i.variant_title : "",
        image_url: i.image || i.featured_image && (i.featured_image.url || i.featured_image) || "",
        category: (i.product_type || "Other"),
        units: parseInt(i.quantity, 10) || 1,
        unit_price: 0,
      };
    });

    var payload = {
      name: (fd.get("name") || "").toString().trim(),
      contact_name: (fd.get("name") || "").toString().trim(),
      email: (fd.get("email") || "").toString().trim(),
      phone: (fd.get("phone") || "").toString().trim(),
      affiliation: (fd.get("affiliation") || "Ambassador").toString().trim(),
      ship_address1: (fd.get("ship_address1") || "").toString().trim(),
      ship_city: (fd.get("ship_city") || "").toString().trim(),
      ship_state: (fd.get("ship_state") || "").toString().toUpperCase().trim(),
      ship_postal: (fd.get("ship_postal") || "").toString().trim(),
      notes: (fd.get("notes") || "").toString().trim(),
      lines: lines,
    };

    STATE.submitting = true;
    btn.disabled = true;
    btn.textContent = "Submitting…";

    fetch(PROTON_API_BASE + "/api/comp/checkout-draft", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, json: j }; })
          .catch(function () { return { ok: r.ok, json: {} }; });
      })
      .then(function (resp) {
        if (!resp.ok || !resp.json || !resp.json.ok) {
          throw new Error((resp.json && resp.json.error) || "Submission failed.");
        }
        showSuccess(resp.json, payload);
        // Clear the cart on the storefront so the ambassador can start
        // fresh next time.
        return fetch("/cart/clear.js", { method: "POST", credentials: "same-origin" });
      })
      .catch(function (err) {
        STATE.submitting = false;
        btn.disabled = false;
        btn.textContent = "Submit for review";
        errorEl.textContent = err.message || "Something went wrong — please try again.";
        errorEl.style.display = "block";
      });
  }

  function showSuccess(resp, payload) {
    var overlay = document.getElementById("amb-overlay");
    if (!overlay) return;
    var name = ((payload.name || "").split(/\s+/)[0]) || "friend";
    overlay.innerHTML =
      '<div class="amb-modal">' +
        '<div class="amb-success">' +
          '<div class="amb-check">&#10003;</div>' +
          '<h2>Sent for review</h2>' +
          '<p style="margin:8px 0 0;color:#f6f2ff;font-size:14.5px;line-height:1.5;">Thanks ' + esc(name) + ' — the Proton team will confirm your gear and ship it out. You&#39;ll get a confirmation email when it&#39;s on the way.</p>' +
          '<div class="amb-order">PROTON HUB &middot; DRAFT #' + esc(resp.id || resp.draft_order_id || "—") + '</div>' +
          '<div class="amb-actions" style="margin-top:20px;justify-content:center;">' +
            '<a class="amb-btn amb-btn-ghost" href="/collections/ambassador-gear" style="text-decoration:none;display:inline-flex;align-items:center;justify-content:center;">Back to gear</a>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function boot() {
    // If the URL alone signals ambassador context, hide payment surfaces
    // synchronously — don't wait on the /cart.js round-trip, because
    // Shop Pay / Apple Pay buttons render quickly and we don't want them
    // flashing before we vanish them.
    if (urlIsAmbassador() || readAmbSession()) {
      writeAmbSession();
      injectStyles();
    }
    fetchCart().then(function (cart) {
      if (inAmbassadorContext(cart)) activate(cart || {});
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
